if (!customElements.get('bottom-sheet-section')) {
	const NUDGE_STORAGE_KEY = 'bf_bottom_sheet_nudge_seen';
	let nudgeSeenInMemory = false;

	class BottomSheetSection extends HTMLElement {
		// How far below the top the sheet must drop before the content stops
		// being a scroller (see hasLeftTop). Pixels.
		static LEAVE_TOP_MARGIN = 32;

		// Share of a programmatic move's own distance that must be covered
		// before the content stops being a scroller (see hasMovedOffTop). The
		// content's jump back to its start is unavoidable — a clipped box
		// resets visually — so it has to happen with the panel clearly on its
		// way out, not in the frame of the tap (review round 5, attack 48).
		// Of the move's distance, not of the sheet's height: the × button
		// travels the whole height, but re-tapping the open button only
		// travels down to the intermediate snap, and a share of the height is
		// then reached at the very end of that move — or never (review round 6,
		// attack 62: on 320×568 and 360×640 with the shop's own snap setting
		// the text jumped with the panel already standing still).
		static LEAVE_MOTION_RATIO = 0.3;

		connectedCallback() {
			this.bottomSheet = this.querySelector('bottom-sheet');
			this.cartDrawer = document.getElementById('CartDrawer');
			this.readMoreButtons = this.querySelectorAll('[data-read-more-trigger]');
			this.snapPoints = Array.from(this.bottomSheet.querySelectorAll('[slot="snap"]'));
			this.snapPointsValues = this.snapPoints.map(
				snapPoint => parseFloat(getComputedStyle(snapPoint).getPropertyValue('--snap'))
			);
			this.slideOutTimeout = null;
			this.firstDelay = parseInt(this.getAttribute('first-delay')) * 1000 || 90000;
			this.slideOutDuration = parseInt(this.getAttribute('slide-out-duration')) || 350;
			this.hideStickyWhenOpen = this.dataset.hideStickyWhenOpen !== 'false';
			this.handleWindowLoad = this.handleWindowLoad.bind(this);
			this.handleManualInteraction = this.handleManualInteraction.bind(this);
			this.handleSectionClick = this.handleSectionClick.bind(this);
			this.handleOpenStateEvent = this.handleOpenStateEvent.bind(this);
			this.handleMobileSnapChange = this.handleMobileSnapChange.bind(this);
			this.handleBottomSheetOpenStateScroll = FoxTheme.utils.debounce(
				this.updateMobileOpenClass.bind(this),
				100
			);
			this.handleBottomSheetScrollEnd = this.updateMobileOpenClass.bind(this);
			this.handlePreventOvershoot = this.handlePreventOvershoot.bind(this);
			this.handleTouchStart = this.handleTouchStart.bind(this);
			this.handleTouchEnd = this.handleTouchEnd.bind(this);
			this.handleContentFocus = this.handleContentFocus.bind(this);

			// Сразу привести признак активности к действительному положению
			// панели: узел мог приехать с ним в разметке (перерисовка секции
			// копией живого DOM), и тогда затвор не включился бы вовсе.
			this.syncSheetActive();

			this.addEventListener('click', this.handleSectionClick);
			this.addEventListener('bottom-sheet-open', this.handleOpenStateEvent);
			this.addEventListener('bottom-sheet-close', this.handleOpenStateEvent);

			if (this.cartDrawer) {
				customElements.whenDefined('cart-drawer').then(() => {
					this.cartDrawer.addEventListener('toggle', () => this.#smoothScrollTo(0));
				});
			}

			if (this.readMoreButtons.length > 0) {
				this.readMoreButtons.forEach(button => {
					button.addEventListener('click', this.handleReadMoreClick.bind(this));
				});
			}

			this.bottomSheet = this.querySelector('bottom-sheet');
			if (this.bottomSheet) {
				this.snapPoint = this.bottomSheet.querySelector('[slot="snap"]:first-child');
			}

			this.triggerButton = this.querySelector('.bottom-sheet__trigger');

			if (this.triggerButton) {
				this.triggerButton.addEventListener('click', this.handleTriggerClick.bind(this));
			}

			if (this.bottomSheet !== null) {
				this.bottomSheet.addEventListener('snap-position-change', this.handleMobileSnapChange);
				this.bottomSheet.addEventListener('scroll', this.handleBottomSheetOpenStateScroll, { passive: true });
				// scrollend fires once any native scroll-snap settling has
				// actually finished — the debounced 'scroll' handler above can
				// fire a beat before that settle completes.
				this.bottomSheet.addEventListener('scrollend', this.handleBottomSheetScrollEnd, { passive: true });
				// A hard/fast swipe can carry momentum scrolling past the
				// collapsed snap point (78px) down to a literal scrollTop of 0
				// before the snap-back settles — real-device + Web Inspector
				// testing traced the disappearing-toolbar freeze to exactly that
				// overshoot (the whole tab's renderer got stuck, independent of
				// any CSS/JS here — confirmed via getComputedStyle vs. actual
				// paint diverging even on document.body). Clamping the floor in
				// JS stops the container from ever reaching that scrollTop, so
				// the gesture can no longer reach whatever internal state
				// triggers it. Skipped while a finger is actually down so this
				// doesn't fight an in-progress drag, and skipped for the
				// deliberate programmatic close (#smoothScrollTo(0)).
				//
				// Since the toolbar moved out of the sheet the collapsed snap
				// point is 0, so this clamp no longer has anything to clamp —
				// it is kept as a floor in case the snap point is ever raised
				// again from the theme editor. The freeze it guarded against
				// needed a sticky toolbar inside the sheet, which is gone.
				this.bottomSheet.addEventListener('scroll', this.handlePreventOvershoot, { passive: true });
				this.bottomSheet.addEventListener('touchstart', this.handleTouchStart, { passive: true });
				this.bottomSheet.addEventListener('touchend', this.handleTouchEnd, { passive: true });
				this.bottomSheet.addEventListener('touchcancel', this.handleTouchEnd, { passive: true });
				this.bottomSheet.addEventListener('focusin', this.handleContentFocus);
				this.setupSlideOut();
			}
		}

		disconnectedCallback() {
			clearTimeout(this.slideOutTimeout);
			clearTimeout(this.touchSettleTimeout);
			this.clearPendingLeave();
			this.programmaticLowering = false;
			this.bottomSheet?.removeAttribute('data-expanded');
			window.removeEventListener('load', this.handleWindowLoad);
			this.removeEventListener('click', this.handleSectionClick);
			this.removeEventListener('bottom-sheet-open', this.handleOpenStateEvent);
			this.removeEventListener('bottom-sheet-close', this.handleOpenStateEvent);

			if (this.bottomSheet && this.debouncedHandleBottomSheetScroll) {
				this.bottomSheet.removeEventListener('scroll', this.debouncedHandleBottomSheetScroll);
			}

			if (this.bottomSheet) {
				this.bottomSheet.removeEventListener('snap-position-change', this.handleMobileSnapChange);
				this.bottomSheet.removeEventListener('scroll', this.handleBottomSheetOpenStateScroll);
				this.bottomSheet.removeEventListener('scrollend', this.handleBottomSheetScrollEnd);
				this.bottomSheet.removeEventListener('scroll', this.handlePreventOvershoot);
				this.bottomSheet.removeEventListener('touchstart', this.handleTouchStart);
				this.bottomSheet.removeEventListener('touchend', this.handleTouchEnd);
				this.bottomSheet.removeEventListener('touchcancel', this.handleTouchEnd);
				this.bottomSheet.removeEventListener('focusin', this.handleContentFocus);
				this.bottomSheet.removeEventListener('pointerdown', this.handleManualInteraction);
				this.bottomSheet.removeEventListener('touchstart', this.handleManualInteraction);
				this.bottomSheet.removeEventListener('focusin', this.handleManualInteraction);
			}

			this.setOpenClass(false);
		}

		handleTriggerClick(event) {
			event.stopPropagation();

			if (!this.bottomSheet) return;

			this.markNudgeAsSeen();
			this.dispatchOpenEvent("Button");
			this.#smoothScrollTo(this.snapPointsValues[0]);
		}

		// Keyboard entry into the panel (Shift+Tab back from the dock lands on
		// the panel's LAST focus stop). Focusing an element below the fold makes
		// the browser scroll the host to the top — the sheet is now fully up —
		// but the content box is still clipped until the settle handlers run, so
		// the focused control stays off-screen (review round 2, attack 25). Take
		// the fully-up state right away and bring the control into view.
		handleContentFocus(event) {
			const target = event.target;
			if (!this.bottomSheet || !(target instanceof Element)) return;
			if (!window.matchMedia('(max-width: 639px)').matches) return;
			// A tap on plain text inside the panel focuses the host itself
			// (<bottom-sheet tabindex="0">); its box is the whole sheet, which
			// always "overflows" the dock and would nudge the content by the
			// dock's height on every such tap (review round 4, attack 44). Only
			// a control inside the content needs bringing into view.
			if (target === this.bottomSheet) return;

			requestAnimationFrame(() => {
				this.syncExpandedState();
				if (!this.bottomSheet.hasAttribute('data-expanded')) return;

				// The visible part of the panel ends at the docked toolbar, not at
				// the bottom of the screen: scrollIntoView aligns to the scrollport
				// and would park the control under the opaque dock (review round 3,
				// attack 30d). Scroll the content so the control clears the dock.
				const dock = document.querySelector('.bottom-sheet__dock');
				const limit = dock ? dock.getBoundingClientRect().top : window.innerHeight;
				const rect = target.getBoundingClientRect();
				if (rect.bottom <= limit && rect.top >= 0) return;

				const content = this.bottomSheet.shadowRoot?.querySelector('.sheet-content');
				if (!content) {
					target.scrollIntoView({ block: 'nearest' });
					return;
				}
				const overflowBelow = rect.bottom - limit;
				const overflowAbove = -rect.top;
				content.scrollTop += overflowBelow > 0 ? overflowBelow + 8 : -(overflowAbove + 8);
			});
		}

		handleReadMoreClick(event) {
			event.stopPropagation();
			
			const button = event.target.closest('[data-read-more-trigger]');
			const excerpt = button.previousElementSibling;
			const content = button.nextElementSibling;
			const firstContentChild = content.firstElementChild;

			excerpt.hidden = true;
			button.hidden = true;
			content.hidden = false;
			content.style.display = 'contents';
			firstContentChild.setAttribute('tabindex', '-1');
			setTimeout(() => {
				firstContentChild.focus();
			}, 0);
		}

		handleSectionClick(event) {
			const target = event.target instanceof Element ? event.target : null;
			if (!target) return;

			const collapseButton = target.closest('[data-bottom-sheet-collapse]');
			if (collapseButton) {
				event.stopPropagation();
				this.markNudgeAsSeen();
				this.#smoothScrollTo(0);
				return;
			}

			const bonusLink = target.closest('[data-bottom-sheet-bonus-club-click]');
			if (bonusLink) {
				this.pushDataLayer('bottom_sheet_bonus_club_click', {
					card_name: bonusLink.dataset.cardName || 'Bonus Club card',
					source: 'bottom_sheet',
				});
				return;
			}

			const infoToggle = target.closest('[data-bottom-sheet-info-toggle]');
			if (infoToggle) {
				event.preventDefault();
				this.toggleInfoCard(infoToggle);
				return;
			}

			if (target.closest('a, input, select, textarea, label, button')) return;

			const infoCard = target.closest('[data-bottom-sheet-info-card]');
			if (!infoCard) return;

			const button = infoCard.querySelector('[data-bottom-sheet-info-toggle]');
			if (button) {
				this.toggleInfoCard(button);
			}
		}

		toggleInfoCard(button) {
			const infoCard = button.closest('[data-bottom-sheet-info-card]');
			if (!infoCard) return;

			const isExpanded = infoCard.classList.toggle('is-expanded');
			button.setAttribute('aria-expanded', String(isExpanded));
			this.syncInfoCardBodyHeight(infoCard, isExpanded);

			if (isExpanded && window.matchMedia('(max-width: 639px)').matches) {
				this.querySelectorAll('[data-bottom-sheet-info-card].is-expanded').forEach((card) => {
					if (card === infoCard) return;
					card.classList.remove('is-expanded');
					card.querySelector('[data-bottom-sheet-info-toggle]')?.setAttribute('aria-expanded', 'false');
					this.syncInfoCardBodyHeight(card, false);
				});
			}

			this.pushDataLayer('bottom_sheet_info_card_expand', {
				card_name: infoCard.dataset.cardName || 'Info card',
				action: isExpanded ? 'expand' : 'collapse',
				source: 'bottom_sheet',
			});
		}

		syncInfoCardBodyHeight(infoCard, isExpanded) {
			const body = infoCard.querySelector('.bottom-sheet__info-body');
			if (!body) return;

			// The stylesheet caps the expanded body at a fixed max-height; long
			// content (e.g. the shipping estimate card) can exceed it, so size
			// the open state to the real content height instead. scrollHeight
			// is 0 while the sheet/drawer copy is display:none — keep the CSS
			// fallback in that case.
			body.style.maxHeight = isExpanded && body.scrollHeight > 0 ? `${body.scrollHeight}px` : '';
		}

		handleOpenStateEvent(event) {
			if (event.type === 'bottom-sheet-open') {
				this.setOpenClass(true);
				return;
			}

			this.setOpenClass(false);
		}

		handleMobileSnapChange() {
			this.updateMobileOpenClass();
		}

		updateMobileOpenClass() {
			if (!this.bottomSheet || !window.matchMedia('(max-width: 639px)').matches) return;

			const collapsedSnap = this.snapPointsValues?.[2] ?? 0;
			this.setOpenClass(this.bottomSheet.scrollTop > collapsedSnap + 1);
			this.syncExpandedState();
		}

		// Expand-to-scroll (see ::part(content) in bottom-sheet.css): the panel's
		// content may only scroll once the sheet is fully up. "Fully up" is
		// scrollTop reaching the sheet's own offset, which equals the host's
		// height (the spacer ahead of the sheet is one sheet-max-height tall) —
		// not scrollHeight − clientHeight, which the component's relatively
		// positioned snap markers inflate past the reachable range on small
		// screens. Not re-evaluated mid-drag: the state settles with the sheet.
		syncExpandedState() {
			if (!this.bottomSheet || this.isTouching) return;

			const sheet = this.bottomSheet;
			const expanded = this.isAtTop();
			const wasExpanded = sheet.hasAttribute('data-expanded');

			if (expanded === wasExpanded) return;

			if (expanded) {
				// Not while a programmatic move away from the top is in flight —
				// its first scroll events still read as "at the top".
				if (this.programmaticLowering) return;
				sheet.setAttribute('data-expanded', '');
				return;
			}

			if (!this.hasLeftTop()) return;
			this.leaveExpandedState();
		}

		// How far a programmatic move must have carried the sheet before its
		// armed leave fires: enough travel for the eye to read the panel as
		// moving, so the content's jump back to its start is covered by that
		// motion rather than landing in the frame of the tap. A fraction of the
		// host so it holds on every screen.
		hasMovedOffTop() {
			const sheet = this.bottomSheet;
			const travel = sheet.clientHeight - (this.programmaticTarget ?? 0);
			const gate = Math.max(24, travel * BottomSheetSection.LEAVE_MOTION_RATIO);
			return sheet.scrollTop <= sheet.clientHeight - gate;
		}

		// "Fully up": the sheet's scroll offset has reached the sheet's own
		// height (see syncExpandedState for why not scrollHeight − clientHeight).
		isAtTop() {
			const sheet = this.bottomSheet;
			return !!sheet && sheet.clientHeight > 0 && sheet.scrollTop >= sheet.clientHeight - 1;
		}

		// The armed leave and the content's non-capturing state are one thing:
		// the attribute must never outlive the flag, or the panel would be left
		// with text nobody can tap.
		armPendingLeave() {
			this.pendingLeave = true;
			this.bottomSheet?.setAttribute('data-lowering', '');
		}

		clearPendingLeave() {
			this.pendingLeave = false;
			this.bottomSheet?.removeAttribute('data-lowering');
		}

		endProgrammaticScroll() {
			// Снимается раньше раннего выхода: обычное открытие с нуля ничего не
			// взводит, выход сработал бы, и признак хода жил бы до страховочного
			// таймера — всё это время закрытая панель оставалась бы целью жеста
			// (ревью, раунд 9).
			this.moveInFlight = false;
			if (!this.programmaticLowering && !this.pendingLeave) return;
			// A move that never produced a scroll event (already at the target,
			// reduced motion) still has to honour its armed leave — unless a
			// finger is down, where touch-end picks it up, or the move was
			// overridden and the sheet is back at the top, where there is
			// nothing to leave and the reading position stays.
			if (this.pendingLeave && !this.isTouching) {
				this.clearPendingLeave();
				if (!this.isAtTop()) this.leaveExpandedState();
			}
			this.programmaticLowering = false;
			this.syncExpandedState();
		}

		// Hysteresis for leaving the fully-up state. Mandatory snap returns the
		// sheet to the top from anywhere above the midpoint to the next snap
		// point below (≈121 px on a Pixel 5), so a reader who pulls the handle
		// down 50–130 px and lets go ends exactly where they started — and must
		// keep their place. Only past that midpoint will the snap carry the
		// sheet down, and only then does the content stop being a scroller
		// (review round 2 attack 15b, round 3 attack 27a). LEAVE_TOP_MARGIN is
		// the floor for a snap point that sits closer than that.
		hasLeftTop() {
			const sheet = this.bottomSheet;
			const top = sheet.clientHeight;
			// Only snap points the engine can see: the nudge's slot sits in the
			// markup with `hidden` and is shown just for the nudge animation —
			// counting it here put the threshold at the wrong midpoint whenever
			// the intermediate snap lies above the host (small screens with a
			// high editor setting; review round 4, attack 39).
			const below = (this.snapPoints || [])
				.map((el, i) => (el.hidden ? NaN : this.snapPointsValues?.[i]))
				.filter((v) => Number.isFinite(v) && v < top - 1);
			const nextSnap = below.length ? Math.max(...below) : 0;
			const margin = Math.max(BottomSheetSection.LEAVE_TOP_MARGIN, (top - nextSnap) / 2);
			return sheet.scrollTop < top - margin;
		}

		// Entering the fully-up state waits for the sheet to settle (see
		// syncExpandedState); leaving it does not. The moment the sheet drops
		// below the top — by a drag, the × button, a re-tap on the open button —
		// the content must stop being a scroller, finger down or not: a slow
		// drag on the handle would otherwise carry a live, half-read scroller
		// down with it for as long as the finger stays (review round 2, attack
		// 15). The host is the thing being dragged, so flipping the content box
		// underneath cannot disturb the gesture.
		leaveExpandedState() {
			const sheet = this.bottomSheet;
			if (!sheet || !sheet.hasAttribute('data-expanded')) return;

			// A partly open panel always shows its start: whatever the content
			// was scrolled to while fully up would otherwise come back the next
			// time the panel is raised. The reset has to happen BEFORE the
			// attribute goes — once the box is `overflow: clip` it is no longer a
			// scroll container, scrollTop reads 0 and an assignment is ignored,
			// while the engine quietly keeps the old offset and restores it when
			// the box turns back into a scroller (adversarial review round 2,
			// attack 18: 303 → "0" → 303 in Chromium, WebKit and Firefox alike).
			const content = sheet.shadowRoot?.querySelector('.sheet-content');
			if (content) content.scrollTop = 0;
			sheet.removeAttribute('data-expanded');
		}

		// Затвор для CSS: закрытая панель не должна быть целью жеста вовсе
		// (см. bottom-sheet.css). Признак собственный и ставится безусловно —
		// ни настройка темы, ни settle-обработчик на него не влияют, иначе
		// панель однажды откроется, но ввода не примет.
		markSheetActive() {
			this.bottomSheet?.setAttribute('data-active', '');
		}

		syncSheetActive() {
			const sheet = this.bottomSheet;
			if (!sheet) return;
			const collapsedSnap = this.snapPointsValues?.[2] ?? 0;
			if (sheet.scrollTop > collapsedSnap + 1) {
				sheet.setAttribute('data-active', '');
				return;
			}
			// Первые кадры плавного хода от нуля ещё стоят на свёрнутом снапе:
			// снять признак здесь значило бы погасить панель ровно в начале
			// её собственного открытия.
			if (this.moveInFlight) return;
			sheet.removeAttribute('data-active');
		}

		setOpenClass(isOpen) {
			if (!this.hideStickyWhenOpen) return;
			document.documentElement.classList.toggle('bottom-sheet-is-open', Boolean(isOpen));
		}

		pushDataLayer(eventName, params = {}) {
			window.dataLayer?.push({
				event: eventName,
				language: window.Shopify?.locale || document.documentElement.lang || null,
				market: window.Shopify?.country || null,
				device_type: window.matchMedia('(max-width: 639px)').matches ? 'mobile' : 'desktop',
				...params,
			});
		}

		setupSlideOut() {
			if (!this.bottomSheet) return;

			if (this.isNudgeSeen()) return;
			if (this.prefersReducedMotion()) {
				this.markNudgeAsSeen();
				return;
			}

			if (document.readyState === 'complete') {
				this.scheduleSlideOut(this.firstDelay);
			} else {
				window.addEventListener('load', this.handleWindowLoad, { once: true });
			}

			this.debouncedHandleBottomSheetScroll = FoxTheme.utils.debounce(
				this.handleBottomSheetScroll.bind(this), 
				200
			);

			this.bottomSheet.addEventListener('scroll', this.debouncedHandleBottomSheetScroll);
			this.bottomSheet.addEventListener('pointerdown', this.handleManualInteraction);
			this.bottomSheet.addEventListener('touchstart', this.handleManualInteraction, { passive: true });
			this.bottomSheet.addEventListener('focusin', this.handleManualInteraction);
		}

		handleBottomSheetScroll() {
			if (this.bottomSheet.scrollTop >= this.snapPointsValues[0]) {
				this.markSlideOutAsShown();
			};
		}

		handleManualInteraction() {
			this.markNudgeAsSeen();
			this.nudgeInterrupted = true;
		}

		handleTouchStart() {
			this.isTouching = true;
		}

		handleTouchEnd() {
			this.isTouching = false;
			// A finger that held the sheet still and then lifted produces no
			// further scroll events, so the settle handlers above never run again;
			// re-evaluate once the release has had a moment to settle.
			clearTimeout(this.touchSettleTimeout);
			this.touchSettleTimeout = setTimeout(() => {
				if (this.pendingLeave) {
					this.clearPendingLeave();
					this.leaveExpandedState();
				}
				this.updateMobileOpenClass();
			}, 150);
		}

		handlePreventOvershoot() {
			if (!this.bottomSheet) return;

			// Первый кадр любого движения — здесь, поэтому затвор снимается
			// раньше, чем палец успевает дотянуться до панели.
			this.syncSheetActive();

			// Leaving the fully-up position must switch the content back to a
			// clipped box on the very scroll event that leaves it — finger down
			// or not, and not 100 ms later with the debounced settle handler: a
			// programmatic move away from the top (the × button, the open button
			// tapped again) followed by a touch inside that window would
			// otherwise still find a live inner scroller. Entering the fully-up
			// position stays with the settle handlers so a drag can't flip it
			// mid-gesture.
			// An armed programmatic leave fires once the panel has covered enough
			// of its own distance, so the content's jump to its start happens
			// under motion rather than in the frame of the tap. Input landing
			// before then cannot reach the content at all — it is not a target
			// while the move is armed — so the wait costs nothing.
			// …but not while a finger is on the sheet: a programmatic move that
			// starts under a resting finger would otherwise wipe the reading
			// position beneath it (review round 5, attack 52) — there the
			// touch-end settle performs the leave instead.
			if (this.pendingLeave && !this.isTouching && this.hasMovedOffTop()) {
				this.clearPendingLeave();
				this.leaveExpandedState();
			}

			// The user out-argued the move and carried the panel back to the
			// top: the leave has lost its reason, and the content must become a
			// target again at once rather than at the backstop timer.
			if (this.pendingLeave && this.isAtTop()) this.clearPendingLeave();

			// The midpoint rule above belongs to a sheet the finger is moving: it
			// is reached a third of the way into a programmatic move, which would
			// put the content's jump right next to the tap that started the move.
			// While such a move has its own leave armed, that leave decides.
			if (
				!this.pendingLeave &&
				this.bottomSheet.hasAttribute('data-expanded') &&
				this.hasLeftTop()
			) {
				this.leaveExpandedState();
			}

			// Ход окончен, как только панель на месте — и для обычного открытия
			// тоже, у которого нет ни взведённого выхода, ни гарда.
			if (
				this.moveInFlight &&
				Math.abs(this.bottomSheet.scrollTop - (this.programmaticTarget ?? 0)) <= 2
			) {
				this.moveInFlight = false;
				this.syncSheetActive();
			}

			// The move is over when the sheet is at its destination, whatever
			// the nudge's animation setting says.
			if (
				this.programmaticLowering &&
				Math.abs(this.bottomSheet.scrollTop - (this.programmaticTarget ?? 0)) <= 2
			) {
				this.endProgrammaticScroll();
			}

			if (this.isTouching || this.allowScrollToZero) return;

			const collapsedSnap = this.snapPointsValues?.[2] ?? 0;
			if (this.bottomSheet.scrollTop < collapsedSnap) {
				this.bottomSheet.scrollTop = collapsedSnap;
			}
		}

		handleWindowLoad() {
			this.scheduleSlideOut(this.firstDelay);
		}

		dispatchOpenEvent(trigger) {
			this.dispatchEvent(new CustomEvent("bottom-sheet-open", {
				bubbles: true,
				detail: {
					drawerType: "Mobile",
					trigger
				}
			}));

			try {
				if (localStorage.getItem("bottom-sheet-slide-out-shown") === null) {
					localStorage.setItem("bottom-sheet-slide-out-shown", "true");
				}
			} catch (error) {
				// Legacy localStorage flag is best-effort only.
			}
		}

		markSlideOutAsShown() {
			this.markNudgeAsSeen();
			try {
				localStorage.setItem('bottom-sheet-slide-out-shown', 'true');
			} catch (error) {
				// Legacy localStorage flag is best-effort only.
			}

			this.dispatchOpenEvent("Scroll");

			if (this.debouncedHandleBottomSheetScroll) {
				this.bottomSheet.removeEventListener('scroll', this.debouncedHandleBottomSheetScroll);
			}
		}

		scheduleSlideOut(delay) {
			clearTimeout(this.slideOutTimeout);
			this.slideOutTimeout = setTimeout(() => this.showSlideOut(), delay);
		}

		async showSlideOut() {
			if (!this.canShowSlideOut()) {
				if (!this.isNudgeSeen() && !this.prefersReducedMotion()) {
					this.scheduleSlideOut(5000);
				}
				return;
			}

			if (Math.floor(this.bottomSheet.scrollTop) > this.snapPointsValues[2]) return;

			if (this.isNudgeSeen()) return;

			this.markNudgeAsSeen();
			this.nudgeInterrupted = false;

			this.snapPoints[1].hidden = false;
			this.markSheetActive();

			// Intentionally no pointer-events lock here: a real touch/drag on the
			// sheet during this animation must be able to grab and redirect it
			// immediately, not be swallowed until the peek finishes. If the user
			// does grab it, nudgeInterrupted (set by handleManualInteraction)
			// stops us from yanking the sheet back to closed under their thumb.
			await this.#smoothScrollTo(this.snapPointsValues[1]);

			if (!this.nudgeInterrupted) {
				await this.#smoothScrollTo(0);
			}

			this.snapPoints[1].hidden = true;
		}

		canShowSlideOut() {
			if (!this.bottomSheet || this.isNudgeSeen()) return false;
			if (this.prefersReducedMotion()) return false;
			if (document.hidden) return false;
			if (!window.matchMedia('(max-width: 639px)').matches) return false;
			if (this.isFormFieldFocused()) return false;
			if (this.isAnyDrawerOpen()) return false;
			if (Math.floor(this.bottomSheet.scrollTop) > this.snapPointsValues[2]) return false;

			return true;
		}

		isAnyDrawerOpen() {
			if (document.body.classList.contains('modal-show') || document.body.classList.contains('modal-showing')) {
				return true;
			}

			if (this.cartDrawer?.open || this.cartDrawer?.hasAttribute('open') || this.cartDrawer?.hasAttribute('active')) {
				return true;
			}

			return Boolean(document.querySelector('dialog[open], drawer-component[open], basic-modal[open]'));
		}

		isFormFieldFocused() {
			const activeElement = document.activeElement;
			if (!(activeElement instanceof Element)) return false;

			return Boolean(activeElement.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])'));
		}

		prefersReducedMotion() {
			return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		}

		isNudgeSeen() {
			try {
				return window.sessionStorage.getItem(NUDGE_STORAGE_KEY) !== null;
			} catch (error) {
				return nudgeSeenInMemory;
			}
		}

		markNudgeAsSeen() {
			nudgeSeenInMemory = true;
			clearTimeout(this.slideOutTimeout);

			// The listener below exists to count a manual drag past the open
			// point as "nudge seen" and to report it as a scroll-open. Once the
			// nudge is seen by any other route — the button, a tap on the sheet —
			// it has nothing left to tell us, and left in place it reported the
			// button's own programmatic scroll as a second drawer_open(scroll).
			if (this.bottomSheet && this.debouncedHandleBottomSheetScroll) {
				this.bottomSheet.removeEventListener('scroll', this.debouncedHandleBottomSheetScroll);
			}

			try {
				window.sessionStorage.setItem(NUDGE_STORAGE_KEY, 'true');
			} catch (error) {
				// In-memory flag above covers browsers that block sessionStorage.
			}
		}

		#smoothScrollTo(targetTop) {
			// The overshoot clamp in handlePreventOvershoot() would otherwise
			// fight this exact call when closing all the way (targetTop 0) —
			// this is the one legitimate way scrollTop should reach 0.
			if (targetTop <= 0) this.allowScrollToZero = true;

			// A programmatic move knows it is leaving the top (the × button, the
			// open button tapped again on a fully-up panel), so it must not wait
			// for the drag hysteresis midpoint — a swipe landing in that window
			// went to the still-live content and the panel stayed where it was
			// (review round 4, attack 45a). But it must not fire in the frame of
			// the tap either: the panel has not moved yet, so the reader sees the
			// text jump to the top under a motionless panel (review round 5,
			// attack 48; the same missing distance gate is attack 52 when a
			// finger is down). The leave is armed here and performed by the
			// scroll handler as soon as the panel has visibly started moving.
			// Only when a fully-up panel is being carried down — an ordinary open
			// (collapsed → 410) has nothing to leave.
			// Ход начинается с этого кадра: панель обязана стать целью ввода до
			// того, как поедет, иначе первые кадры она видна, но не принимает жест.
			this.markSheetActive();
			// Ход, которому некуда ехать, не порождает ни одного scroll-события,
			// значит признак хода некому снять — и затвор остался бы открытым на
			// весь страховочный таймер (450 мс при магазинной настройке, 1100 мс
			// при максимуме схемы). Живой путь сюда один — слушатель toggle на
			// корзине-дровере, а эта секция выключена; но одна галочка в
			// редакторе вернула бы дефект без единой строчки кода
			// (ревью, раунд 10).
			this.moveInFlight = Math.abs(this.bottomSheet.scrollTop - targetTop) > 2;
			this.programmaticTarget = targetTop;
			if (!this.moveInFlight) this.syncSheetActive();
			if (
				this.bottomSheet &&
				this.bottomSheet.hasAttribute('data-expanded') &&
				targetTop < this.bottomSheet.clientHeight - 1
			) {
				this.armPendingLeave();
				// Keep the fully-up state from coming back while the move runs:
				// its first scroll events still read as "at the top" and the
				// settle handlers would re-enter it for a frame (−@630 +@630
				// −@503 in review round 4, attacks 16/34), resetting twice.
				this.programmaticLowering = true;
			}

			return new Promise((resolve) => {
				this.bottomSheet.scrollTo({
					top: targetTop,
					behavior: this.prefersReducedMotion() ? 'auto' : 'smooth'
				});

				// Backstop only. The guard is normally cleared the moment the
				// sheet reaches its destination (see handlePreventOvershoot):
				// slide-out-duration describes the nudge animation, not how long
				// a programmatic scroll takes — tying the guard to it froze the
				// state for 689 ms at the setting's maximum while the real move
				// had settled in ~90 ms (review round 5, attacks 47/53).
				setTimeout(() => {
					this.allowScrollToZero = false;
					this.moveInFlight = false;
					this.endProgrammaticScroll();
					this.syncSheetActive();
					resolve();
				}, this.slideOutDuration + 100);
			});
		}
	}

	customElements.define('bottom-sheet-section', BottomSheetSection);
}
